import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';


const ROWS_PER_PAGE = 25;


const DebitNoteList = () => {
  const [debitNotes, setDebitNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();


  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    debitNoteNo: '',
    dateFrom: '',
    dateTo: '',
    buyer: '',
    salesContract: ''
  });


  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);


  useEffect(() => {
    fetchDebitNotes();
    // eslint-disable-next-line
  }, [page, debouncedSearch, filters]);


  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1);
  };


  const resetFilters = () => {
    setFilters({
      debitNoteNo: '',
      dateFrom: '',
      dateTo: '',
      buyer: '',
      salesContract: ''
    });
    setSearch('');
    setPage(1);
  };


  const fetchDebitNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim()
      });
  
      if (filters.debitNoteNo) {
        params.append('debitNoteNo', filters.debitNoteNo);
      }
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }
      if (filters.buyer) {
        params.append('buyer', filters.buyer);
      }
      if (filters.salesContract) {
        params.append('salesContract', filters.salesContract);
      }
  
      const response = await fetch(
        `${API_BASE_URL}/api/sales/debit-note-get?${params}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      
      // Debug logging
      console.log('API Response:', data);
      console.log('Pagination object:', data.pagination);
      console.log('Total from pagination:', data.pagination?.total);
      
      if (response.ok) {
        setDebitNotes(data.data || []);
        // ✅ CORRECT: Access total from pagination object
        setTotal(data.pagination?.total || 0);
      } else {
        setError('Failed to load data');
        toast.error(data.error || 'Failed to fetch debit notes');
      }
    } catch (error) {
      console.error('Error fetching debit notes:', error);
      setError('Failed to load data');
      toast.error('Server Error');
    }
    setLoading(false);
  };
  

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));


  const handleViewDebitNote = (debitNoteId) => {
    navigate(`/debit-note/preview/${debitNoteId}`);
  };


  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Debit Note Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Quick Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className={`flex items-center gap-1 px-3 py-1 text-xs rounded font-medium border ${
              showFilters 
                ? 'bg-gray-600 text-white border-gray-700 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={() => navigate('/debit-note/new')}
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>


      {showFilters && (
        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Debit Note No.</label>
              <input
                type="text"
                name="debitNoteNo"
                placeholder="Enter Debit Note No."
                value={filters.debitNoteNo}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Buyer</label>
              <input
                type="text"
                name="buyer"
                placeholder="Search buyer..."
                value={filters.buyer}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sales Contract</label>
              <input
                type="text"
                name="salesContract"
                placeholder="Search contract..."
                value={filters.salesContract}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <button
              onClick={resetFilters}
              className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium"
            >
              Reset All Filters
            </button>
          </div>
        </div>
      )}


      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : debitNotes.length === 0 ? (
        <NoRecordFound message="No debit note records found." />
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-gray-50">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Debit Note No</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Date</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Sales Contract</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Contract DN of Account</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Buyer Name</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Buyer GST</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">CMA/CHA Name</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Issuing Authority</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Warehouse Name</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Warehouse Location</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State Code</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">KABL Business Name</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Particulars</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Amount</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">GST Amount</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Total Amount</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {debitNotes.map((debitNote, i) => (
                  <tr key={debitNote.debit_note_id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-1 font-semibold whitespace-nowrap">{debitNote.debit_note_number}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{formatDateDDMMYY(debitNote.debit_note_date)}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.sales_contract_no}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.sales_contract_dn_no || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.buyer_name}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.buyer_gst || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.cha_cma_name || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.issuing_authority_name || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.warehouse_name || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.warehouse_location || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.state_code || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.kabl_business_name || '-'}</td>
                    <td className="px-3 py-1">
                      <div 
                        className="truncate max-w-32 whitespace-nowrap" 
                        title={debitNote.particulars || '-'}
                      >
                        {debitNote.particulars || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap">₹{parseFloat(debitNote.amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-1 whitespace-nowrap">₹{parseFloat(debitNote.gst_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-1 whitespace-nowrap font-semibold">₹{parseFloat(debitNote.total_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{debitNote.check_cma_cha || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDebitNote(debitNote.debit_note_id)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                        title="View Debit Note"
                      >
                        <FaEye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination - Now inside the data block and with better condition */}
          {(total > ROWS_PER_PAGE || totalPages > 1) && (
            <div className="flex justify-end items-center gap-2 mt-3 text-xs">
              <button
                onClick={handlePrev}
                disabled={page === 1}
                className={`px-2 py-1 rounded border transition-colors duration-100 ${
                  page === 1 
                    ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed' 
                    : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
                }`}
              >
                Prev
              </button>
              <span className="text-gray-600">
                Page {page} of {totalPages} ({total} total records)
              </span>
              <button
                onClick={handleNext}
                disabled={page === totalPages}
                className={`px-2 py-1 rounded border transition-colors duration-100 ${
                  page === totalPages 
                    ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed' 
                    : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}


      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
      />
    </div>
  );
};


export default DebitNoteList;
